import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, AbstractControl, ValidationErrors } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatStepperModule } from '@angular/material/stepper';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { DataService } from '../../services/data.service';
import { AuthService } from '../../services/auth.service';
import { Region, Industry, Country, RegistrationAvailablePlan } from '../../models/registration';

@Component({
  selector: 'app-registration-wizard',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatStepperModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatSnackBarModule
  ],
  templateUrl: './registration-wizard.component.html',
  styleUrl: './registration-wizard.component.scss'
})
export class RegistrationWizardComponent implements OnInit {
  currentStep = 1;
  isLoading = false;
  errorMessage = '';
  marketplaceToken: string | null = null;

  // Forms for each step
  emailForm: FormGroup;
  verificationForm: FormGroup;
  passwordForm: FormGroup;
  userInfoForm: FormGroup;
  companyForm: FormGroup;
  planForm: FormGroup;

  // Lookup data
  regions: Region[] = [];
  industries: Industry[] = [];
  countries: Country[] = [];
  availablePlans: RegistrationAvailablePlan[] = [];
  selectedRegion: Region | null = null;
  selectedPlanKey: string | null = null;

  // State tracking
  hidePassword = true;
  hidePasswordConfirm = true;
  codeResendCooldown = 0;
  private resendTimer: any;

  // Password strength getter
  get passwordStrength(): number {
    return this.calculatePasswordStrength();
  }

  constructor(
    private fb: FormBuilder,
    private dataService: DataService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private snackBar: MatSnackBar
  ) {
    // Initialize forms
    this.emailForm = this.fb.group({
      email: ['', [Validators.required, Validators.email, this.emailValidator]]
    });

    this.verificationForm = this.fb.group({
      code: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]]
    });

    this.passwordForm = this.fb.group({
      password: ['', [Validators.required, Validators.minLength(8), this.passwordComplexityValidator]],
      passwordConfirm: ['', Validators.required]
    }, { validators: this.passwordMatchValidator });

    this.userInfoForm = this.fb.group({
      firstName: ['', [Validators.required, Validators.minLength(2)]],
      lastName: ['', [Validators.required, Validators.minLength(2)]]
    });

    this.companyForm = this.fb.group({
      regionId: ['', Validators.required],
      companyName: ['', [Validators.required, Validators.minLength(2)]],
      industryId: [''],
      primaryContactName: ['', Validators.required],
      primaryContactEmail: ['', [Validators.required, Validators.email]],
      address1: [''],
      address2: [''],
      city: [''],
      postcode: [''],
      countryId: ['', Validators.required]
    });

    this.planForm = this.fb.group({
      planId: ['', Validators.required]
    });
  }

  ngOnInit(): void {
    // Check for marketplace token
    this.route.queryParams.subscribe(params => {
      if (params['token']) {
        this.marketplaceToken = params['token'];
      }
    });

    // Load lookup data
    this.loadLookupData();
  }

  async loadLookupData(): Promise<void> {
    try {
      this.regions = await this.dataService.getRegions().toPromise() || [];
      this.industries = await this.dataService.getIndustries().toPromise() || [];
      this.countries = await this.dataService.getCountries().toPromise() || [];
      
      // Set default region
      const defaultRegion = this.regions.find(r => r.isDefault);
      if (defaultRegion) {
        this.selectedRegion = defaultRegion;
        this.companyForm.patchValue({ regionId: defaultRegion.regionId });
      }
    } catch (error: any) {
      console.error('Error loading lookup data:', error);
      this.showError('Failed to load registration data. Please try again.');
    }
  }

  // =============================================
  // STEP 1: EMAIL ENTRY
  // =============================================

  async goToVerification(): Promise<void> {
    if (this.emailForm.invalid) return;

    this.isLoading = true;
    this.errorMessage = '';

    try {
      const email = this.emailForm.get('email')?.value;
      const result = await this.dataService.sendVerificationCode(email).toPromise();
      
      if (result?.success) {
        this.currentStep = 2;
        this.startResendCooldown();
        this.showSuccess('Verification code sent to your email');
      } else {
        this.errorMessage = result?.message || 'Failed to send verification code';
      }
    } catch (error: any) {
      this.errorMessage = error.error?.message || error.error?.Message || 'Failed to send verification code. Please try again.';
    } finally {
      this.isLoading = false;
    }
  }

  // =============================================
  // STEP 2: EMAIL VERIFICATION
  // =============================================

  async verifyCode(): Promise<void> {
    if (this.verificationForm.invalid) return;

    this.isLoading = true;
    this.errorMessage = '';

    try {
      const email = this.emailForm.get('email')?.value;
      const code = this.verificationForm.get('code')?.value;
      const result = await this.dataService.verifyEmailCode(email, code).toPromise();
      
      if (result?.valid) {
        this.currentStep = 3;
        this.showSuccess('Email verified successfully');
      } else {
        this.errorMessage = result?.message || 'Invalid verification code';
      }
    } catch (error: any) {
      this.errorMessage = error.error?.message || error.error?.Message || 'Invalid verification code. Please try again.';
    } finally {
      this.isLoading = false;
    }
  }

  async resendCode(): Promise<void> {
    if (this.codeResendCooldown > 0) return;

    try {
      const email = this.emailForm.get('email')?.value;
      const result = await this.dataService.sendVerificationCode(email).toPromise();
      
      if (result?.success) {
        this.startResendCooldown();
        this.showSuccess('Verification code resent');
      }
    } catch (error: any) {
      this.showError('Failed to resend code. Please try again.');
    }
  }

  startResendCooldown(): void {
    this.codeResendCooldown = 60;
    this.resendTimer = setInterval(() => {
      this.codeResendCooldown--;
      if (this.codeResendCooldown <= 0) {
        clearInterval(this.resendTimer);
      }
    }, 1000);
  }

  // =============================================
  // STEP 3: PASSWORD CREATION & USER REGISTRATION
  // =============================================

  async registerUser(): Promise<void> {
    if (this.passwordForm.invalid) return;

    this.isLoading = true;
    this.errorMessage = '';

    try {
      const email = this.emailForm.get('email')?.value;
      const password = this.passwordForm.get('password')?.value;
      
      // Register user
      const registerResult = await this.dataService.registerUser(email, password).toPromise();
      
      if (registerResult?.Success) {
        // Authenticate user to get token
        const authResult = await this.dataService.authenticate({
          userName: email,
          password: password
        }).toPromise();
        
        if (authResult?.token) {
          // Store in AuthService
          this.authService.setCurrentUser({
            userId: authResult.userId || '',
            username: authResult.userName || email,
            firstName: authResult.firstName || '',
            lastName: authResult.lastName || '',
            companyName: authResult.companyName || '',
            token: authResult.token
          });
          
          this.currentStep = 4;
          this.showSuccess('Account created successfully');
        }
      } else {
        this.errorMessage = registerResult?.Message || 'Failed to register user';
      }
    } catch (error: any) {
      this.errorMessage = error.error?.message || error.error?.Message || 'Registration failed. Please try again.';
    } finally {
      this.isLoading = false;
    }
  }

  calculatePasswordStrength(): number {
    const password = this.passwordForm.get('password')?.value || '';
    let strength = 0;
    
    if (password.length >= 8) strength += 25;
    if (password.length >= 12) strength += 15;
    if (/[a-z]/.test(password)) strength += 25;
    if (/[A-Z]/.test(password)) strength += 25;
    if (/\d/.test(password)) strength += 25;
    
    return Math.min(strength, 100);
  }

  getPasswordStrengthClass(): string {
    const strength = this.calculatePasswordStrength();
    if (strength < 40) return 'weak';
    if (strength < 70) return 'medium';
    return 'strong';
  }

  // =============================================
  // STEP 4: USER INFORMATION
  // =============================================

  async submitUserInfo(): Promise<void> {
    if (this.userInfoForm.invalid) return;

    this.isLoading = true;
    this.errorMessage = '';

    try {
      const result = await this.dataService.updateUserProfile({
        FirstName: this.userInfoForm.get('firstName')?.value,
        LastName: this.userInfoForm.get('lastName')?.value
      }).toPromise();
      
      if (result?.success) {
        // Update current user
        const currentUser = this.authService.getCurrentUser();
        if (currentUser) {
          this.authService.setCurrentUser({
            ...currentUser,
            firstName: this.userInfoForm.get('firstName')?.value || '',
            lastName: this.userInfoForm.get('lastName')?.value || ''
          });
        }
        
        // Pre-populate company form
        this.populateCompanyDefaults();
        this.currentStep = 5;
        this.showSuccess('Profile updated successfully');
      } else {
        this.errorMessage = result?.message || 'Failed to update profile';
      }
    } catch (error: any) {
      this.errorMessage = error.error?.message || error.error?.Message || 'Failed to update profile. Please try again.';
    } finally {
      this.isLoading = false;
    }
  }

  populateCompanyDefaults(): void {
    const firstName = this.userInfoForm.get('firstName')?.value || '';
    const lastName = this.userInfoForm.get('lastName')?.value || '';
    const fullName = `${firstName} ${lastName}`.trim();
    const email = this.emailForm.get('email')?.value || '';
    
    this.companyForm.patchValue({
      primaryContactName: fullName,
      primaryContactEmail: email
    });
  }

  // =============================================
  // STEP 5: COMPANY SETUP
  // =============================================

  onRegionChange(): void {
    const regionId = this.companyForm.get('regionId')?.value;
    this.selectedRegion = this.regions.find(r => r.regionId === regionId) || null;
    
    // Load plans for this region
    if (this.selectedRegion) {
      this.loadAvailablePlans(this.selectedRegion.code);
    }
  }

  async loadAvailablePlans(regionCode: string): Promise<void> {
    try {
      this.availablePlans = await this.dataService.getRegistrationAvailablePlans(regionCode).toPromise() || [];
      
      // Auto-select first plan if only one available
      if (this.availablePlans.length === 1) {
        this.planForm.patchValue({ planId: this.availablePlans[0].planId });
      }
    } catch (error: any) {
      console.error('Error loading plans:', error);
      this.showError('Failed to load available plans');
    }
  }

  async submitCompany(): Promise<void> {
    if (this.companyForm.invalid) return;

    this.isLoading = true;
    this.errorMessage = '';

    try {
      const formValue = this.companyForm.value;
      const result = await this.dataService.createCompany({
        RegionId: formValue.regionId,
        CompanyName: formValue.companyName,
        IndustryId: formValue.industryId || undefined,
        PrimaryContactName: formValue.primaryContactName,
        PrimaryContactEmail: formValue.primaryContactEmail,
        Address1: formValue.address1 || undefined,
        Address2: formValue.address2 || undefined,
        City: formValue.city || undefined,
        Postcode: formValue.postcode || undefined,
        CountryId: formValue.countryId
      }).toPromise();
      
      if (result?.success) {
        // Update current user
        const currentUser = this.authService.getCurrentUser();
        if (currentUser) {
          this.authService.setCurrentUser({
            ...currentUser,
            companyName: formValue.companyName
          });
        }
        
        this.currentStep = 6;
        this.showSuccess('Company created successfully');
      } else {
        this.errorMessage = result?.message || 'Failed to create company';
      }
    } catch (error: any) {
      this.errorMessage = error.error?.message || error.error?.Message || 'Failed to create company. Please try again.';
    } finally {
      this.isLoading = false;
    }
  }

  // =============================================
  // STEP 6: PLAN SELECTION
  // =============================================

  async submitPlan(): Promise<void> {
    if (this.planForm.invalid) return;

    this.isLoading = true;
    this.errorMessage = '';

    try {
      const planId = this.planForm.get('planId')?.value;
      
      // If there's a marketplace token, handle marketplace activation
      if (this.marketplaceToken) {
        // Subscribe to marketplace plan
        const result = await this.dataService.activateMarketplaceSubscription({
          marketplaceSubscriptionId: '', // Will be populated from token resolution
          token: this.marketplaceToken,
          subscriptionStatusTypeId: 1,
          addSubscribeUserJustAsOwner: true
        }).toPromise();
        
        if (result?.internalSubscriptionId) {
          this.currentStep = 7;
          await this.completeRegistration();
        }
      } else {
        // Regular plan subscription
        const result = await this.dataService.subscribeToPlan({
          Product: 'DQP',
          PlanId: planId,
          AddSubscribeUserJustAsOwner: false
        }).toPromise();
        
        if (result?.success) {
          this.currentStep = 7;
          await this.completeRegistration();
        } else {
          this.errorMessage = result?.message || 'Failed to subscribe to plan';
        }
      }
    } catch (error: any) {
      this.errorMessage = error.error?.message || error.error?.Message || 'Failed to subscribe to plan. Please try again.';
    } finally {
      this.isLoading = false;
    }
  }

  // =============================================
  // STEP 7: COMPLETION
  // =============================================

  async completeRegistration(): Promise<void> {
    try {
      // Refresh user plan
      const plan = await this.dataService.getUserPlan().toPromise();
      if (plan) {
        this.authService.setUserPlan(plan);
      }
      
      this.showSuccess('Registration completed successfully!');
      
      // Redirect to dashboard after short delay
      setTimeout(() => {
        this.router.navigate(['/dashboard']);
      }, 2000);
    } catch (error: any) {
      console.error('Error completing registration:', error);
      // Still redirect to dashboard even if plan fetch fails
      setTimeout(() => {
        this.router.navigate(['/dashboard']);
      }, 2000);
    }
  }

  // =============================================
  // NAVIGATION
  // =============================================

  goBack(): void {
    if (this.currentStep > 1) {
      this.currentStep--;
      this.errorMessage = '';
    }
  }

  skipStep(): void {
    // Some steps can be skipped
    if (this.currentStep === 5 && this.companyForm.get('address1')?.value === '') {
      this.currentStep++;
    }
  }

  // =============================================
  // VALIDATORS
  // =============================================

  emailValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;
    
    const email = control.value.toLowerCase();
    
    // Common typos
    const typos = [
      { wrong: '@gmial.com', correct: '@gmail.com' },
      { wrong: '@gmai.com', correct: '@gmail.com' },
      { wrong: '@yahooo.com', correct: '@yahoo.com' },
      { wrong: '@outlok.com', correct: '@outlook.com' }
    ];
    
    for (const typo of typos) {
      if (email.includes(typo.wrong)) {
        return { emailTypo: { value: control.value, suggestion: email.replace(typo.wrong, typo.correct) } };
      }
    }
    
    return null;
  }

  passwordComplexityValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;
    
    const password = control.value;
    const hasLetter = /[a-zA-Z]/.test(password);
    const hasNumber = /\d/.test(password);
    
    if (!hasLetter || !hasNumber) {
      return { passwordComplexity: true };
    }
    
    return null;
  }

  passwordMatchValidator(group: AbstractControl): ValidationErrors | null {
    const password = group.get('password')?.value;
    const confirmPassword = group.get('passwordConfirm')?.value;
    
    if (password && confirmPassword && password !== confirmPassword) {
      return { passwordMismatch: true };
    }
    
    return null;
  }

  // =============================================
  // UTILITY METHODS
  // =============================================

  togglePasswordVisibility(): void {
    this.hidePassword = !this.hidePassword;
  }

  togglePasswordConfirmVisibility(): void {
    this.hidePasswordConfirm = !this.hidePasswordConfirm;
  }

  showSuccess(message: string): void {
    this.snackBar.open(message, 'Close', {
      duration: 3000,
      panelClass: ['success-snackbar']
    });
  }

  showError(message: string): void {
    this.snackBar.open(message, 'Close', {
      duration: 5000,
      panelClass: ['error-snackbar']
    });
  }

  ngOnDestroy(): void {
    if (this.resendTimer) {
      clearInterval(this.resendTimer);
    }
  }
}
